/*
  Split band/cup picker for bras and swimwear. Shopify holds the size as one "Size" option
  ("12F"); this element shows it as Band + Cup buttons and drives the hidden stock
  hdt-variant-picker, which updates price, stock line, buttons and URL.

  - Only buyable sizes are shown (in stock or pre-order). Bands with nothing buyable are hidden.
  - The shopper's cup is never swapped for them. If it isn't buyable in the new band, no cup is
    selected and the buttons ask for one ("Select a cup size"). The cup comes back on a band
    that has it. The cup preselected on page load isn't the shopper's choice, so changing band
    clears it until they pick one.
  - ?size=12F on the URL selects that size (used when switching colour); a size from a link
    that isn't buyable here leaves the cup unselected rather than swapping in another.
  - No note on band or colour changes (removed 7 Oct, felt jarring); the only note is a fully
    sold-out colour.
*/
class HDTSplitVariantPicker extends HTMLElement {
    constructor() {
        super();
        this.variantPickerContainer = this.closest('.hdt-product__variant-picker');
        this.originalPicker = this.variantPickerContainer.querySelector('hdt-variant-picker');
        this.formId = this.getAttribute('form');
        this.scope = this.closest('.hdt-main-product-quick-view') || this.closest('.shopify-section') || document;

        this.bandInputs = this.querySelectorAll('input[name="Band"]');
        this.cupInputs = this.querySelectorAll('input[name="Cup"]');
        this.note = this.querySelector('.split-variant-picker__note');

        this.availableCombinations = new Set(this.dataset.availableCombinations.split(',').filter(Boolean));
        this.preferredCup = null;

        this.setupEventListeners();
        this.initializeSelections();
    }

    setupEventListeners() {
        this.bandInputs.forEach(input => input.addEventListener('change', (e) => {
            e.stopPropagation();
            this.onBandChange();
        }));
        this.cupInputs.forEach(input => input.addEventListener('change', (e) => {
            e.stopPropagation();
            this.onCupChange();
        }));
        // A size picked elsewhere (the sticky add-to-cart bar's dropdown) updates the stock picker; mirror it here
        this.originalPicker?.addEventListener('change', (e) => {
            const input = e.target;
            if (input.type === 'radio' && input.checked && input.value !== this.selectedSize) this.reflect(input.value);
        });
    }

    reflect(size) {
        if (!this.availableCombinations.has(size)) return;
        const [band, cup] = this.splitByFirstAlphabetic(size);
        this.querySelector(`input[name="Band"][value="${band}"]`).checked = true;
        this.renderCups(band);
        this.querySelector(`input[name="Cup"][value="${cup}"]`).checked = true;
        this.preferredCup = cup;
        this.clearIncomplete(size);
        this.updateLegends();
    }

    splitByFirstAlphabetic(str) {
        const firstAlphaIndex = str.search(/[a-zA-Z]/);
        if (firstAlphaIndex === -1) return [str, ''];
        return [str.substring(0, firstAlphaIndex), str.substring(firstAlphaIndex)];
    }

    get selectedBand() { return this.querySelector('input[name="Band"]:checked')?.value; }
    get selectedCup() { return this.querySelector('input[name="Cup"]:checked')?.value; }
    get selectedSize() {
        const band = this.selectedBand, cup = this.selectedCup;
        return band && cup ? `${band}${cup}` : null;
    }

    bandIsBuyable(band) {
        return [...this.availableCombinations].some(size => this.splitByFirstAlphabetic(size)[0] === band);
    }

    initializeSelections() {
        const original = this.variantPickerContainer.querySelector('hdt-variant-picker input[type=radio]:checked');
        if (!original) return;
        let size = original.value;
        // A size from a link (filter, search, another colour) counts as the shopper's own choice
        let shopperChoseSize = this.hasAttribute('deep-linked');

        // ?size= carries the shopper's size over from another colour of the same style
        const url = new URL(window.location.href);
        const requested = url.searchParams.get('size');
        if (requested) {
            shopperChoseSize = true;
            size = requested;
            url.searchParams.delete('size');
            history.replaceState(history.state, '', url.toString());
        }

        if (this.availableCombinations.size === 0) {
            this.classList.add('is-sold-out');
            if (this.note) {
                this.note.textContent = 'This colour is sold out.';
                this.note.hidden = false;
            }
            return;
        }

        this.markBands();
        const [band, cup] = this.splitByFirstAlphabetic(size);
        if (shopperChoseSize) this.preferredCup = cup;

        if (!this.availableCombinations.has(size)) {
            // Keep the shopper's band if it has anything buyable; never pick a different size for them
            const bandInput = this.bandIsBuyable(band) && this.querySelector(`input[name="Band"][value="${band}"]`);
            if (bandInput) bandInput.checked = true;
            this.renderCups(bandInput ? band : null);
            this.updateLegends();
            this.setIncomplete();
            return;
        }

        this.querySelector(`input[name="Band"][value="${band}"]`).checked = true;
        this.renderCups(band);
        this.querySelector(`input[name="Cup"][value="${cup}"]`).checked = true;
        this.updateLegends();
        if (size !== original.value) this.syncOriginal(size);
    }

    // Bands with nothing buyable are hidden, as before
    markBands() {
        this.bandInputs.forEach(input => {
            const buyable = this.bandIsBuyable(input.value);
            input.disabled = !buyable;
            input.nextElementSibling.classList.toggle('hide-option', !buyable);
        });
    }

    // Show only the cups buyable in this band (none until a band is chosen)
    renderCups(band) {
        this.cupInputs.forEach(input => {
            const buyable = !!band && this.availableCombinations.has(`${band}${input.value}`);
            input.disabled = !buyable;
            input.nextElementSibling.classList.toggle('hide-option', !buyable);
        });
    }

    onBandChange() {
        const band = this.selectedBand;
        this.renderCups(band);
        const cupInput = this.preferredCup && this.querySelector(`input[name="Cup"][value="${this.preferredCup}"]`);
        if (cupInput && !cupInput.disabled) {
            cupInput.checked = true;
            this.clearIncomplete(`${band}${this.preferredCup}`);
            this.updateLegends();
            this.syncOriginal(`${band}${this.preferredCup}`);
        } else {
            this.cupInputs.forEach(input => input.checked = false);
            this.updateLegends();
            this.setIncomplete();
        }
    }

    onCupChange() {
        this.preferredCup = this.selectedCup;
        this.clearIncomplete(this.selectedSize);
        this.updateLegends();
        this.syncOriginal(this.selectedSize);
    }

    updateLegends() {
        const bandDisplay = this.querySelector('[update-value-band]');
        const cupDisplay = this.querySelector('[update-value-cup]');
        if (bandDisplay) bandDisplay.textContent = this.selectedBand || '';
        if (cupDisplay) cupDisplay.textContent = this.selectedCup || '';
    }

    // Push the combined size into the hidden stock picker, which handles price, stock, buttons and URL
    syncOriginal(size) {
        const originalInput = size && this.variantPickerContainer.querySelector(`hdt-variant-picker input[value="${size}"]`);
        if (!originalInput) return;
        originalInput.checked = true;
        // On page load the stock picker is still fetching product data and not listening yet
        this.whenOriginalReady().then(() => originalInput.dispatchEvent(new Event('change', { bubbles: true })));
    }

    whenOriginalReady() {
        return new Promise(resolve => {
            const check = (tries) => (!this.originalPicker || this.originalPicker.masterInput || tries > 100) ? resolve() : setTimeout(() => check(tries + 1), 50);
            check(0);
        });
    }

    get buyButtons() {
        return document.querySelectorAll(`hdt-buy-buttons[form="${this.formId}"]`);
    }

    get stickyBar() {
        return document.querySelector(`hdt-sticky-btn-atc[form="${this.formId}"]`);
    }

    get stockLines() {
        return this.scope.querySelectorAll(`hdt-variant-inventory[form="${this.formId}"], .add_to_cart_message`);
    }

    // No size selected: block add to cart / Buy it now and drop the stock line until the shopper picks one
    setIncomplete() {
        this.incomplete = true;
        const form = document.forms[this.formId];
        if (form?.id) form.id.value = '';
        // Let the stock picker re-announce the variant once a cup is chosen, even if it's the same one
        if (this.originalPicker) this.originalPicker.oldVariant = null;
        this.buyButtons.forEach(buttons => {
            buttons.setAttribute('size-incomplete', '');
            const submit = buttons.querySelector('button[type="submit"]');
            if (submit) {
                submit.disabled = true;
                submit.firstElementChild.textContent = this.selectedBand ? 'Select a cup size' : 'Select a size';
            }
        });
        this.stockLines.forEach(el => el.style.display = 'none');

        // The sticky add-to-cart bar has its own size dropdown and button: make it wait too
        const sticky = this.stickyBar;
        const stickySelect = sticky?.querySelector('select[name="id"]');
        const stickyButton = sticky?.querySelector('button[name="add"]');
        if (stickyButton && !this.stickySaved) {
            this.stickySaved = { disabled: stickyButton.disabled, text: stickyButton.firstElementChild.textContent };
        }
        if (stickyButton) {
            stickyButton.disabled = true;
            stickyButton.firstElementChild.textContent = 'Select a size';
        }
        if (stickySelect && !stickySelect.querySelector('option[data-split-placeholder]')) {
            stickySelect.insertAdjacentHTML('afterbegin', '<option value="" data-split-placeholder disabled>Select a size</option>');
        }
        if (stickySelect) stickySelect.value = '';

        // Drop the old size from the address so a refresh doesn't bring it back
        // (after the stock picker's own load-time URL update, which would re-add it)
        this.whenOriginalReady().then(() => {
            const url = new URL(window.location.href);
            if (!this.incomplete || !url.searchParams.has('variant')) return;
            url.searchParams.delete('variant');
            history.replaceState(history.state, '', url.toString());
        });
    }

    // size: the size about to be selected, so the sticky bar can be put back in step
    clearIncomplete(size) {
        if (!this.incomplete) return;
        this.incomplete = false;
        this.buyButtons.forEach(buttons => buttons.removeAttribute('size-incomplete'));
        this.stockLines.forEach(el => el.style.display = '');

        const sticky = this.stickyBar;
        const stickySelect = sticky?.querySelector('select[name="id"]');
        const stickyButton = sticky?.querySelector('button[name="add"]');
        stickySelect?.querySelector('option[data-split-placeholder]')?.remove();
        const option = stickySelect && [...stickySelect.options].find(o => o.text.trim().startsWith(`${size} `));
        if (option) stickySelect.value = option.value;
        // The sticky bar only refreshes when the size differs from its last one; restore it for the same size
        if (stickyButton && this.stickySaved) {
            stickyButton.disabled = this.stickySaved.disabled;
            stickyButton.firstElementChild.textContent = this.stickySaved.text;
        }
        this.stickySaved = null;
    }
}

class HDTSplitVariantPickerQuickView extends HDTSplitVariantPicker {}

customElements.define('hdt-split-variant-picker-quick-view', HDTSplitVariantPickerQuickView);
customElements.define('hdt-split-variant-picker', HDTSplitVariantPicker)
document.querySelectorAll('hdt-split-variant-picker, hdt-split-variant-picker-quick-view').forEach(el => customElements.upgrade(el));

// Colour swatches link to separate products: carry the shopper's band + cup over as ?size=
document.addEventListener('click', (e) => {
    const link = e.target.closest('.hdt-product__linked_products a[href*="/products/"]');
    if (!link || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    let scope = link.parentElement;
    while (scope && !scope.querySelector('hdt-split-variant-picker')) scope = scope.parentElement;
    const size = scope?.querySelector('hdt-split-variant-picker')?.selectedSize;
    if (!size) return;
    const url = new URL(link.href, window.location.origin);
    url.searchParams.set('size', size);
    link.href = url.toString();
}, true);


// "Need help with sizing?" in the split picker opens the Size & fit modal through the hidden stock
// picker's button, which the theme modal is wired to. Delegated, so it works whenever this script
// loads (it's async) and inside quick view without rebinding.
document.addEventListener('click', (e) => {
    const trigger = e.target.closest('hdt-split-variant-picker fieldset.band-size legend button');
    if (!trigger) return;
    const stockButton = trigger.closest('.hdt-product__variant-picker')
        ?.querySelector('hdt-variant-picker .hdt-has-btn-size-chart .hdt-product-form__label button');
    if (!stockButton) return;
    e.preventDefault();
    window.__sizeHelpTrigger = trigger;
    stockButton.click();
});
